import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Segmented, Select, Tooltip } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ContextBar from '../components/common/ContextBar';
import ResourceState from '../components/loading/ResourceState';
import { HOME_CRUMB } from '../utils/breadcrumbs';
import ExportModal from '../components/common/ExportModal';
import GroupHeader from '../components/common/GroupHeader';
import RecordBadge from '../components/common/RecordBadge';
import { CardOptionLabel } from '../components/cards/CardVisuals';
import ItemCardsGrid from '../components/items/ItemCardsGrid';
import ItemsTable from '../components/items/ItemsTable';
import TagBadge, { PRIVATE_NOTE, RESTRICTED_NOTE, TagRow } from '../components/tags/TagBadge';
import useActiveTagMenu from '../components/tags/useActiveTagMenu';
import { GRID, LOCK, PERSON_OUTLINE, PIN_ANGLE, TABLE } from '../components/icons';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import useCollectionIdentity from '../hooks/useCollectionIdentity';
import useTagPage from '../hooks/useTagPage';
import { PIN_KIND } from '../services/favorites';
import { formatCount } from '../utils/counts';
import { normalizeId } from '../utils/ids';
import { cardsHoldRepeatingGroup } from '../utils/groupColumns';
import useWindowClass from '../hooks/useWindowClass';
import {
	isItemTag,
	isPrivateTag,
	isRestrictedTag,
	tagOwnerCollectionId,
	tagPagePath,
	tagParentIdOf,
	tagRecordOf,
} from '../utils/tags';

export const TAG_NOT_FOUND_TITLE = 'Tag not found';
export const TAG_NOT_FOUND_TEXT = 'It may have been deleted, or it isn’t one you can see.';
const EMPTY_TEXT = 'Nothing carries this tag yet — put it on an item from the item’s Tags row.';
const FILTERED_EMPTY_TEXT = 'No items with this tag match these filters.';

const VIEW_MODES = ['grid', 'table'];
const KIND_WORDS = { private: 'Private tag', collection: 'Collection tag', item: 'Item tag' };

const kindOf = tag => (isItemTag(tag) ? 'item' : isPrivateTag(tag) ? 'private' : 'collection');
const nameOf = tag => (isItemTag(tag) ? (tag?.title || 'Untitled item') : (tag?.name || 'Tag'));
const itemPath = (collectionId, itemId) => `/collections/${collectionId}/items/${itemId}`;

/**
 * THE TAG PAGE — `/tags/:tagId` (docs/plans/tags.md §5.2 "Tag page", §3.8;
 * build step T5), for every kind of tag: a private tag, a collection tag, an
 * item tag. Everything that carries the tag — or a tag under it — across every
 * collection the person reaches, read through `GET /api/tags/{id}/items`
 * (hooks/useTagPage).
 *
 *   header    the tag's own surface: its chip and plain name (an item tag its
 *             item's card tile and title), the kind as the kicker, the
 *             containers above it as the breadcrumb (its collection, or
 *             Private tags; then the tags above it). Below: "Only you see
 *             this" on a private tag, the lock's words on a restricted one,
 *             an item tag's item as a record badge that opens it, and how
 *             many items. ONE filled primary: **Pin** (Home and the sidebar);
 *             pinned, it is **Unpin**. The ⋯ goes to the collection, the item
 *             or Private tags; on a private tag it has **Make active…** —
 *             or, while it is the active tag, **Stop adding it** (the active
 *             tag, b52; Record) — and **Export…** (T9, Share & export): the rows
 *             the page shows — its card and collection filters, its view —
 *             as PDF, Excel or JSON, with "Include my private tags".
 *   toolbar   the card filter and the collection filter (each option with how
 *             many carry the tag — the counts never shrink as you filter), and
 *             Grid · Table — all in the URL (`?card=`, `?collection=`,
 *             `?view=table`), so a filtered tag page can be linked to.
 *   children  the tags directly under it, each a chip with its count beside it
 *             (never inside the pill), opening that tag's own page — the list
 *             narrowed to it.
 *   the list  the platform's own tiles (ItemCardsGrid) or table (ItemsTable),
 *             `crossCollection`: every row's title is qualified by the
 *             collection it lives in, and each opens in its own collection.
 *             "Show more" reads the next page.
 *
 * THE ONE 404: a tag that is missing, deleted, someone else's private tag, a
 * parent's restricted tag seen from a sub-collection, or an item tag whose item
 * the person cannot read all answer the same, and so all read the same here —
 * "Tag not found" — whatever the API's reason (it gives none).
 *
 * On a phone the header's actions shrink to squares (the primary keeps its
 * words in its accessible name), the toolbar wraps with each filter a full
 * row, and Grid · Table become glyphs — the collection list's own grammar.
 */
export default function TagPage() {
	const { tagId } = useParams();
	const navigate = useNavigate();
	const [params, setParams] = useSearchParams();
	const cardFilter = normalizeId(params.get('card')) || null;
	const collectionFilter = normalizeId(params.get('collection')) || null;
	const view = VIEW_MODES.includes(params.get('view')) ? params.get('view') : 'grid';
	const { compact: isMobile } = useWindowClass();

	const page = useTagPage(tagId, { cardId: cardFilter, collectionId: collectionFilter });
	const { tag, total, items, cards, collections, children, loading, loadingMore, notFound, error, hasMore, loadMore, reload, replaceItem } = page;

	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions?.cardsById]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const favorites = useOptionalFavorites();
	const id = normalizeId(tag?._id) || normalizeId(tagId);
	const pinned = Boolean(tag && favorites?.isPinned?.(PIN_KIND.TAG, id));

	const kind = tag ? kindOf(tag) : null;
	const item = kind === 'item';
	const itemCard = item ? cardsById[normalizeId(tag.card_id)] || null : null;
	// Where the tag belongs: a collection tag's own collection, an item tag's
	// item's collection now. A private tag belongs to no collection.
	const ownerId = item ? normalizeId(tag.collection_id) : (tag ? tagOwnerCollectionId(tag) : '');
	const owner = useCollectionIdentity(ownerId || null);

	const [exporting, setExporting] = useState(false);
	// A private tag's ⋯ turns it into the active tag, or stops it (b52).
	const activeMenu = useActiveTagMenu();

	const setParam = useCallback((key, value) => {
		setParams(current => {
			const next = new URLSearchParams(current);
			if (value) next.set(key, value); else next.delete(key);
			return next;
		}, { replace: true });
	}, [setParams]);

	// ---- the header ------------------------------------------------------

	const breadcrumb = useMemo(() => {
		if (!tag) return [];
		const crumbs = [];
		if (kind === 'private') crumbs.push({ label: 'Private tags', to: '/account/tags' });
		else if (owner?.name && ownerId) crumbs.push({ label: owner.name, to: `/collections/${ownerId}` });
		// The tags above it: the vocabulary's path names them; the one right
		// above is a door to its own page.
		const path = !item && Array.isArray(tag.path) ? tag.path.slice(0, -1) : [];
		const parentId = tagParentIdOf(tag);
		path.forEach((name, index) => {
			const direct = index === path.length - 1 && parentId;
			crumbs.push({ label: name, ...(direct ? { to: `/tags/${parentId}` } : {}) });
		});
		return crumbs;
	}, [tag, kind, item, owner, ownerId]);

	const below = tag ? (
		<span className="tag-page__meta">
			{kind === 'private' ? (
				<span className="tag-page__note">
					<PERSON_OUTLINE aria-hidden="true" />
					{PRIVATE_NOTE}
				</span>
			) : null}
			{isRestrictedTag(tag) ? (
				<span className="tag-page__note">
					<LOCK aria-hidden="true" />
					{RESTRICTED_NOTE}
				</span>
			) : null}
			{item && tag.item_id && ownerId ? (
				<span className="tag-page__stands-for">
					<span className="tag-page__stands-for-label">Stands for</span>
					<RecordBadge
						kind="item"
						record={itemCard}
						kicker={itemCard ? (itemCard.label?.singular || itemCard.name || undefined) : undefined}
						title={nameOf(tag)}
						size="xs"
						to={itemPath(ownerId, normalizeId(tag.item_id))}
						collection={owner || ownerId}
					/>
				</span>
			) : null}
			{!loading && formatCount(total, 'item') ? (
				<span className="tag-page__count">{formatCount(total, 'item')}</span>
			) : null}
		</span>
	) : null;

	const togglePin = () => {
		if (!favorites?.togglePin || !id) return;
		favorites.togglePin(PIN_KIND.TAG, { targetId: id });
	};
	const pinLabel = pinned ? 'Unpin' : 'Pin';
	const pinTitle = pinned ? 'Unpin from Home and the sidebar' : 'Pin to Home and the sidebar';
	const pinButton = tag && favorites?.togglePin ? (
		<Tooltip title={isMobile ? pinTitle : undefined}>
			<Button
				type={pinned ? 'default' : 'primary'}
				icon={<PIN_ANGLE />}
				aria-pressed={pinned}
				aria-label={isMobile ? pinTitle : undefined}
				title={isMobile ? undefined : pinTitle}
				onClick={togglePin}
				data-testid="tag-page-pin"
			>
				{isMobile ? null : pinLabel}
			</Button>
		</Tooltip>
	) : null;

	const menuActions = tag ? [
		{
			key: 'item',
			group: 'navigate',
			glyph: 'open',
			label: 'Open item',
			hidden: !(item && tag.item_id && ownerId),
			onClick: () => navigate(itemPath(ownerId, normalizeId(tag.item_id))),
		},
		{
			key: 'collection',
			group: 'navigate',
			glyph: 'collection',
			label: owner?.name ? `Go to ${owner.name}` : 'Go to collection',
			hidden: kind === 'private' || !ownerId || !owner,
			onClick: () => navigate(`/collections/${ownerId}`),
		},
		{
			key: 'private-tags',
			group: 'navigate',
			glyph: 'settings',
			label: 'Manage private tags',
			hidden: kind !== 'private',
			onClick: () => navigate('/account/tags'),
		},
		// Make active… / Stop adding it — a private tag's only (Record).
		...(kind === 'private' ? activeMenu.actionsFor(tag) : []),
		{
			key: 'export',
			group: 'share',
			glyph: 'export',
			label: 'Export…',
			onClick: () => setExporting(true),
		},
	] : null;

	// The export is the page as it stands: its filters narrow the file.
	const exportTarget = useMemo(() => (tag && id ? {
		kind: 'tag',
		tagId: id,
		params: {
			...(cardFilter ? { card_id: cardFilter } : {}),
			...(collectionFilter ? { collection_id: collectionFilter } : {}),
		},
	} : null), [tag, id, cardFilter, collectionFilter]);

	// ---- the toolbar -----------------------------------------------------

	const cardOptions = useMemo(() => cards.map(entry => {
		const card = cardsById[normalizeId(entry.card_id)] || null;
		const name = card?.name || card?.label?.singular || 'Card';
		return {
			value: normalizeId(entry.card_id),
			title: name,
			label: (
				<span className="tag-page__option">
					<CardOptionLabel card={card || { _id: entry.card_id, name }} />
					<span className="tag-page__option-count">{entry.count}</span>
				</span>
			),
		};
	}), [cards, cardsById]);

	// Excel asks "one line per entry" only when a card on the page holds a
	// repeating group (KPR-685); narrowed to one card, that card decides.
	const exportOffersGroupShape = useMemo(() => cardsHoldRepeatingGroup(
		cards
			.filter(entry => !cardFilter || normalizeId(entry.card_id) === normalizeId(cardFilter))
			.map(entry => cardsById[normalizeId(entry.card_id)])
			.filter(Boolean),
		getElementsForCard,
	), [cards, cardsById, cardFilter, getElementsForCard]);

	const collectionOptions = useMemo(() => collections.map(entry => {
		const cid = normalizeId(entry.collection_id);
		const name = entry.name || 'Unnamed collection';
		return {
			value: cid,
			title: name,
			label: (
				<span className="tag-page__option">
					<RecordBadge kind="collection" record={{ _id: cid, name }} title={name} size="xs" />
					<span className="tag-page__option-count">{entry.count}</span>
				</span>
			),
		};
	}), [collections]);

	const filterOption = (input, option) => `${option?.title || ''}`.toLowerCase().includes(input.toLowerCase());

	const viewSwitch = (
		<Segmented
			className={`tag-page__view-switch segmented--neutral${isMobile ? ' items-page__view-switch--icons' : ''}`}
			value={view}
			onChange={value => setParam('view', value === 'grid' ? null : value)}
			options={isMobile ? [
				{ value: 'grid', title: 'Grid', icon: <GRID role="img" aria-label="Grid" /> },
				{ value: 'table', title: 'Table', icon: <TABLE role="img" aria-label="Table" /> },
			] : [
				{ value: 'grid', label: 'Grid' },
				{ value: 'table', label: 'Table' },
			]}
		/>
	);

	const toolbar = tag ? (
		<div className="tag-page__toolbar">
			<Select
				className="tag-page__filter"
				allowClear
				showSearch
				placeholder="All cards"
				aria-label="Card"
				value={cardFilter || undefined}
				options={cardOptions}
				optionLabelProp="title"
				filterOption={filterOption}
				onChange={value => setParam('card', value || null)}
				popupMatchSelectWidth={false}
				notFoundContent="No cards"
			/>
			<Select
				className="tag-page__filter"
				allowClear
				showSearch
				placeholder="All collections"
				aria-label="Collection"
				value={collectionFilter || undefined}
				options={collectionOptions}
				optionLabelProp="title"
				filterOption={filterOption}
				onChange={value => setParam('collection', value || null)}
				popupMatchSelectWidth={false}
				notFoundContent="No collections"
			/>
			<span className="tag-page__toolbar-spacer" />
			{viewSwitch}
		</div>
	) : null;

	// ---- the page --------------------------------------------------------

	if (notFound) {
		return (
			<div className="tag-page tag-page--missing">
				<ContextBar title={TAG_NOT_FOUND_TITLE} breadcrumb={[HOME_CRUMB]} />
				<ResourceState state="not-found" body={TAG_NOT_FOUND_TEXT} backTo="/" backLabel="Go to Home" />
			</div>
		);
	}

	const childList = Array.isArray(children) ? children.filter(child => child?.tag) : [];
	const filtered = Boolean(cardFilter || collectionFilter);
	const emptyText = loading ? null : (filtered ? FILTERED_EMPTY_TEXT : EMPTY_TEXT);

	return (
		<div className="tag-page" data-tag-kind={kind || undefined}>
			<ContextBar
				breadcrumb={breadcrumb}
				kicker={tag ? KIND_WORDS[kind] : null}
				record={tag ? (item ? itemCard : tagRecordOf(tag)) : undefined}
				kind={item ? 'item' : 'tag'}
				title={tag ? nameOf(tag) : null}
				below={below}
				actions={pinButton}
				menuActions={menuActions}
			>
				{toolbar}
			</ContextBar>

			{error ? (
				<Alert
					type="warning"
					showIcon
					message="Couldn’t load this tag’s items."
					action={<Button size="small" onClick={reload}>Try again</Button>}
				/>
			) : null}

			{childList.length ? (
				<section className="tag-page__children" aria-label="Tags under it">
					<GroupHeader label="Tags under it" count={childList.length} />
					<TagRow
						className="tag-page__child-row"
						label="Tags under it"
						tags={childList.map(child => child.tag)}
						renderTag={(childTag) => {
							const entry = childList.find(child => child.tag === childTag);
							const count = entry?.count || 0;
							return (
								<span className="tag-page__child">
									<TagBadge
										tag={childTag}
										size="sm"
										to={tagPagePath(childTag) || undefined}
										resolveItemTag={isItemTag(childTag) ? () => ({
											record: cardsById[normalizeId(childTag.card_id)] || null,
											title: childTag.title || 'Untitled item',
										}) : undefined}
									/>
									{count > 0 ? (
										<span className="tag-page__child-count" aria-label={formatCount(count, 'item')}>{count}</span>
									) : null}
								</span>
							);
						}}
					/>
				</section>
			) : null}

			<section className="tag-page__list" aria-label="Items">
				{view === 'table' ? (
					<>
						<ItemsTable
							items={items}
							cardsById={cardsById}
							getElementsForCard={getElementsForCard}
							loading={loading}
							crossCollection
						/>
						{!loading && !items.length && emptyText ? <p className="tag-page__empty">{emptyText}</p> : null}
					</>
				) : (
					<ItemCardsGrid
						items={items}
						cardsById={cardsById}
						getElementsForCard={getElementsForCard}
						loading={loading}
						emptyText={emptyText}
						onItemUpdated={replaceItem}
						crossCollection
					/>
				)}
				{hasMore ? (
					<div className="tag-page__more">
						<Button onClick={loadMore} loading={loadingMore}>Show more</Button>
					</div>
				) : null}
			</section>
			{kind === 'private' ? activeMenu.dialog : null}
			{exporting && exportTarget ? (
				<ExportModal
					open={exporting}
					onClose={() => setExporting(false)}
					target={exportTarget}
					title={`Export “${nameOf(tag)}”`}
					baseName={nameOf(tag)}
					supportsExcel
					listLayout={view === 'table' ? 'table' : 'grid'}
					offersGroupShape={exportOffersGroupShape}
				/>
			) : null}
		</div>
	);
}
